import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ReactDOM from "react-dom";

/* -----------------------------
    UTILITY: Default Rules
----------------------------- */
// Default rules structure
const DEFAULT_RULES = {
  ratingScale: [
    { label: "1 - Poor", value: 1 },
    { label: "2 - Below Average", value: 2 },
    { label: "3 - Meets Expectation", value: 3 },
    { label: "4 - Exceeds Expectation", value: 4 },
    { label: "5 - Outstanding", value: 5 },
  ],
  formula: "(KRA_SCORE * 0.6) + (OKR_SCORE * 0.4)",
  normalization: {
    enabled: false,
    type: "bell-curve",
    topPercent: 10,
    lowPercent: 10,
  },
};

/* -----------------------------
    MODAL COMPONENT (Preserved)
----------------------------- */
const Modal = ({ open, onClose, title, children, width = "max-w-3xl" }) => {
  if (!open) return null;

  return ReactDOM.createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm flex justify-center"
        style={{ paddingTop: "100px", alignItems: "flex-start" }}
      >
        <motion.div
          initial={{ y: 20, scale: 0.96 }}
          animate={{ y: 0, scale: 1 }}
          exit={{ y: 20, scale: 0.96 }}
          className={`bg-white w-full ${width} rounded-2xl shadow-xl flex flex-col max-h-[75vh] overflow-hidden`}
        >
          <div className="p-4 border-b bg-white sticky top-0 z-20 flex items-center justify-between">
            <h2 className="text-lg font-semibold">{title}</h2>
            <button
              className="text-slate-600 hover:text-black"
              onClick={onClose}
            >
              ✕
            </button>
          </div>

          <div className="overflow-y-auto flex-1 px-6 py-4">{children}</div>

          <div className="p-4 border-t bg-white sticky bottom-0">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-slate-200 hover:bg-slate-300 rounded-md"
            >
              Close
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

/* -----------------------------
    SCORING RULES MAIN APP
----------------------------- */
export default function App() {
  const LS_KEY = "performance_scoring_rules";
  const [rules, setRules] = useState(null); 
  const [isLoading, setIsLoading] = useState(true);
  // Changed saveMessage to a simple string state for inline use
  const [saveMessage, setSaveMessage] = useState(null); 
  const [previewOpen, setPreviewOpen] = useState(false);

  // 1. Load Rules from localStorage on initial mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        setRules({ ...DEFAULT_RULES, ...JSON.parse(raw) });
      } else {
        setRules(DEFAULT_RULES);
      }
    } catch (e) {
      console.error("Error loading rules from localStorage:", e);
      setRules(DEFAULT_RULES);
    } finally {
      setIsLoading(false);
    }
  }, []);


  // 2. Handlers to Update Local Rules State
  const updateRatingScale = useCallback((i, patch) => {
    if (!rules) return;
    const updated = [...rules.ratingScale];
    updated[i] = { ...updated[i], ...patch };
    setRules({ ...rules, ratingScale: updated });
  }, [rules]);

  const addRating = useCallback(() => {
    if (!rules) return;
    setRules({
      ...rules,
      ratingScale: [
        ...rules.ratingScale,
        { label: "New Rating", value: rules.ratingScale.length + 1 },
      ],
    });
  }, [rules]);

  const removeRating = useCallback((i) => {
    if (!rules) return;
    if (rules.ratingScale.length <= 1) {
      setSaveMessage("Cannot remove the last rating level.");
      // Clear error message after 5 seconds
      setTimeout(() => setSaveMessage(null), 5000);
      return;
    }
    const updated = rules.ratingScale.filter((_, idx) => idx !== i);
    setRules({ ...rules, ratingScale: updated });
  }, [rules]);


  // 3. Functional Save Logic (Writes to localStorage and sets inline message)
  const handleSave = () => {
    if (!rules) {
      setSaveMessage("Application data not initialized.");
      setTimeout(() => setSaveMessage(null), 5000);
      return;
    }

    try {
      localStorage.setItem(LS_KEY, JSON.stringify(rules));
      setSaveMessage("Scoring rules saved successfully!");
      // Clear the message after 3 seconds
      setTimeout(() => {
        setSaveMessage(null);
      }, 3000); 

    } catch (error) {
      console.error("Error saving rules to localStorage:", error);
      setSaveMessage("Failed to save rules locally.");
      // Clear error message after 5 seconds
      setTimeout(() => setSaveMessage(null), 5000);
    }
  };
  
  // Display a loading indicator while data is being fetched
  if (isLoading || !rules) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        <p className="ml-4 text-slate-600">Loading Configuration...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6 font-[Poppins,Roboto,sans-serif]">
      <style>
        {`@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;600&family=Roboto:wght@300;400;500;600;700&display=swap');`}
      </style>

      <div className="max-w-6xl mx-auto space-y-8">
        {/* Page Title */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-[Roboto]">
              Scoring Rules
            </h1>
            <p className="text-sm text-slate-600 mt-1 font-[Poppins]">
              Define how performance scores are calculated and rated.
            </p>
          </div>

          <button
            onClick={() => setPreviewOpen(true)}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg shadow font-[Roboto] font-medium transition"
          >
            Preview Scoring
          </button>
        </div>

        {/* Rating Scale */}
        <section className="bg-white p-6 rounded-2xl shadow-md space-y-4">
          <h2 className="text-xl font-semibold text-slate-800 font-[Roboto]">
            Rating Scale
          </h2>

          <div className="space-y-3">
            {rules.ratingScale.map((r, i) => (
              <div
                key={i}
                className="flex items-center gap-3 p-3 bg-slate-50 border rounded-lg"
              >
                <input
                  value={r.label}
                  onChange={(e) =>
                    updateRatingScale(i, { label: e.target.value })
                  }
                  className="flex-1 px-3 py-2 border rounded-md font-[Poppins]"
                  placeholder="Label (e.g., '3 - Meets Expectation')"
                />
                <input
                  type="number"
                  value={r.value}
                  onChange={(e) =>
                    updateRatingScale(i, { value: Number(e.target.value) })
                  }
                  className="w-20 px-3 py-2 border rounded-md text-center font-[Poppins]"
                  min="1"
                />

                <button
                  onClick={() => removeRating(i)}
                  className="px-2 py-1 text-rose-600 hover:bg-rose-50 rounded transition"
                  disabled={rules.ratingScale.length <= 1}
                >
                  ✕
                </button>
              </div>
            ))}

            <button
              onClick={addRating}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-[Roboto] font-medium transition"
            >
              + Add Rating Level
            </button>
          </div>
        </section>

        {/* Formula Section */}
        <section className="bg-white p-6 rounded-2xl shadow-md space-y-3">
          <h2 className="text-xl font-semibold text-slate-800 font-[Roboto]">
            Score Calculation Formula
          </h2>

          <input
            value={rules.formula}
            onChange={(e) => setRules({ ...rules, formula: e.target.value })}
            className="w-full px-3 py-3 border rounded-md font-mono text-sm font-[Poppins]"
            placeholder="(SCORE_A * 0.7) + (SCORE_B * 0.3)"
          />

          <p className="text-xs text-slate-500 font-[Poppins]">
            Example: <b>(KRA_SCORE * 0.6) + (OKR_SCORE * 0.4)</b>
          </p>
        </section>

        {/* Normalization Rules */}
        <section className="bg-white p-6 rounded-2xl shadow-md space-y-4">
          <h2 className="text-xl font-semibold text-slate-800 font-[Roboto]">
            Normalization Settings
          </h2>

          <label className="flex items-center gap-3 text-sm font-[Poppins]">
            <input
              type="checkbox"
              checked={rules.normalization.enabled}
              onChange={(e) =>
                setRules({
                  ...rules,
                  normalization: {
                    ...rules.normalization,
                    enabled: e.target.checked,
                  },
                })
              }
            />
            Enable Normalization (Bell Curve)
          </label>

          {rules.normalization.enabled && (
            <div className="grid grid-cols-2 gap-4 max-w-lg">
              <div>
                <label className="text-sm text-slate-600 font-[Poppins]">Top % (High Performers)</label>
                <input
                  type="number"
                  className="w-full px-3 py-2 border rounded-md font-[Poppins]"
                  value={rules.normalization.topPercent}
                  min="0"
                  max="100"
                  onChange={(e) =>
                    setRules({
                      ...rules,
                      normalization: {
                        ...rules.normalization,
                        topPercent: Number(e.target.value),
                      },
                    })
                  }
                />
              </div>

              <div>
                <label className="text-sm text-slate-600 font-[Poppins]">Low % (Bottom Performers)</label>
                <input
                  type="number"
                  className="w-full px-3 py-2 border rounded-md font-[Poppins]"
                  value={rules.normalization.lowPercent}
                  min="0"
                  max="100"
                  onChange={(e) =>
                    setRules({
                      ...rules,
                      normalization: {
                        ...rules.normalization,
                        lowPercent: Number(e.target.value),
                      },
                    })
                  }
                />
              </div>
            </div>
          )}
        </section>

        {/* Save Button & Inline Message */}
        <div className="flex flex-col items-end pt-4">
          <button
            onClick={handleSave}
            // Reduced padding (px-4 py-2) and changed color (bg-sky-600)
            className="px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-lg shadow-md text-base font-bold font-[Roboto] transition transform hover:scale-[1.01]"
          >
            Save Scoring Rules
          </button>
          
          {/* Inline Success/Error Message */}
          <AnimatePresence>
            {saveMessage && (
              <motion.p
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 5 }}
                // Use text-xs for small font, and conditional color based on success/error
                className={`text-xs mt-1 ${saveMessage.includes('successfully') ? 'text-emerald-600' : 'text-rose-600'}`}
              >
                {saveMessage}
              </motion.p>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* PREVIEW MODAL */}
      <Modal open={previewOpen} onClose={() => setPreviewOpen(false)} title="Scoring Preview">
        <div className="space-y-4 font-[Poppins]">
          <h3 className="text-lg font-semibold font-[Roboto]">Rating Scale</h3>
          <ul className="space-y-1">
            {rules.ratingScale.map((r, i) => (
              <li key={i} className="text-sm">
                {r.label} → <b className="text-indigo-600">{r.value}</b>
              </li>
            ))}
          </ul>

          <h3 className="text-lg font-semibold mt-4 font-[Roboto]">Formula</h3>
          <code className="block bg-slate-100 p-3 rounded-md text-sm">
            {rules.formula}
          </code>

          {rules.normalization.enabled && (
            <>
              <h3 className="text-lg font-semibold mt-4 font-[Roboto]">Normalization</h3>
              <p className="text-sm text-slate-600">
                Normalization is **active**: Top <b className="text-emerald-600">{rules.normalization.topPercent}%</b> and Bottom{" "}
                <b className="text-rose-600">{rules.normalization.lowPercent}%</b> of employees will have their scores adjusted to fit the model.
              </p>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}